import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const merchantOid = searchParams.get('merchant_oid');
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    if (!merchantOid) {
      navigate('/credits');
      return;
    }

    // Poll order status for a few seconds to catch callback processing
    const checkStatus = async () => {
      try {
        const response = await api.get(`/paytr/order-status/${merchantOid}`);
        setOrderStatus(response.data);
        
        if (response.data.status === 'completed') {
          setPolling(false);
          setLoading(false);
        } else if (response.data.status === 'failed') {
          setPolling(false);
          setLoading(false);
          navigate(`/payment/failure?merchant_oid=${merchantOid}`);
        }
      } catch (error) {
        console.error('Order status check error:', error);
        setPolling(false);
        setLoading(false);
      }
    };

    checkStatus();

    // Poll every 2 seconds for up to 20 seconds
    let pollCount = 0;
    const maxPolls = 10;
    const pollInterval = setInterval(() => {
      if (pollCount >= maxPolls || !polling) {
        clearInterval(pollInterval);
        setLoading(false);
        return;
      }
      checkStatus();
      pollCount++;
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [merchantOid, navigate, polling]);

  if (!merchantOid) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card>
          <CardHeader>
            <div className="flex flex-col items-center text-center">
              {loading ? (
                <>
                  <Loader2 className="h-16 w-16 text-blue-600 animate-spin mb-4" />
                  <CardTitle className="text-2xl">Ödemeniz İşleniyor...</CardTitle>
                  <p className="text-slate-600 mt-2">
                    Lütfen bekleyin, kredi ekleme işlemi devam ediyor.
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-16 w-16 text-green-600 mb-4" />
                  <CardTitle className="text-2xl text-green-600">Ödeme Başarılı!</CardTitle>
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!loading && orderStatus && (
              <>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-700">
                    <strong>Sipariş No:</strong> {orderStatus.merchant_oid}
                  </p>
                  <p className="text-sm text-slate-700">
                    <strong>Kredi Miktarı:</strong> {orderStatus.credits} kredi
                  </p>
                  <p className="text-sm text-slate-700">
                    <strong>Ödenen Tutar:</strong> {orderStatus.amount_tl} ₺
                  </p>
                  <p className="text-sm text-slate-700">
                    <strong>Durum:</strong> <span className="text-green-600 font-semibold">Tamamlandı</span>
                  </p>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-slate-700">
                    ✅ {orderStatus.credits} kredi hesabınıza başarıyla eklenmiştir.
                  </p>
                  <p className="text-sm text-slate-600 mt-2">
                    Kredi bakiyenizi kontrol edebilir ve soru cevaplama işlemlerine devam edebilirsiniz.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    onClick={() => navigate('/credits')}
                    className="flex-1"
                  >
                    Kredi Bakiyeni Gör
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    variant="outline"
                    className="flex-1"
                  >
                    Dashboard'a Dön
                  </Button>
                </div>
              </>
            )}

            {loading && (
              <div className="text-center text-sm text-slate-600">
                <p>Kredileriniz hesabınıza ekleniyor...</p>
                <p className="mt-2">Bu işlem birkaç saniye sürebilir.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
